import { useState, useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Navigation from '@components/Navigation/Navigation';
import ErrorBoundary from '@components/ErrorBoundary';
import { useAuth } from '@hooks/useAuth';
import { prefetchColdQueries } from '@queries';

interface LayoutProps {
  children?: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const [activeView, setActiveView] = useState('dashboard');
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { workspace } = useAuth();

  // Aquece as queries "frias" compartilhadas assim que o workspace resolve.
  // Pequeno atraso p/ não competir com o fetch da rota atual no 1º paint.
  useEffect(() => {
    if (!workspace?.id) return undefined;
    const t = setTimeout(() => prefetchColdQueries(queryClient, workspace.id), 500);
    return () => clearTimeout(t);
  }, [workspace?.id, queryClient]);

  const handleViewChange = (view: string) => {
    setActiveView(view);
    navigate(`/${view}`);
  };

  useEffect(() => {
    const path = location.pathname.substring(1);
    if (path && path !== activeView) {
      setActiveView(path);
    } else if (!path && activeView !== 'dashboard') {
      setActiveView('dashboard');
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation
        activeView={activeView}
        onViewChange={handleViewChange}
        currentPath={location.pathname}
      />
      <main className="flex-1">
        <ErrorBoundary>
          <Outlet />
          {children}
        </ErrorBoundary>
      </main>
    </div>
  );
};

export default Layout;
